import data from './compliance-status.json';

/**
 * Phase 6 Pass 3 iter 245 — compliance-status source-of-truth loader.
 *
 * Per `pass-3-trust-center.md` § "Compliance status driven by JSON
 * config": "SOC 2 / ISO 27001 / EU AI Act status changes faster
 * than code deploys. The status page reads from a JSON config so
 * an admin can update milestones without a code change."
 *
 * The JSON shape follows the spec literal (snake_case framework
 * keys + per-framework `status` + `target` + `evidence_provider` +
 * `auditor`). Iter 245 extends with `label` (human-readable display
 * name) and a free-text body; both are optional from the spec's
 * perspective and concrete here so card rendering is deterministic.
 *
 * **i18n (this iter)**: the free-text body is no longer stored as
 * inline prose — it's a `notesKey` resolving through `useT()` under
 * the `compliance.row.<framework>.notes` namespace, so the body
 * localizes for non-English visitors. Framework `label`, `target`
 * dates, statute/cert acronyms, percentages, and dollar/duration
 * figures stay verbatim inside the resolved prose.
 *
 * **Status taxonomy (Product hat, locked)**:
 *
 *   - `planned`           — committed but evidence collection not started
 *   - `in_progress`       — evidence being collected / audit in motion
 *   - `available`         — already shipped (e.g. DPA template, CCPA implementation)
 *   - `compliance_verified` — counsel or auditor has signed off (no certification body
 *                              required; e.g. EU AI Act counsel review)
 *   - `certified`         — third-party certificate issued (SOC 2 / ISO 27001 reports)
 *   - `out_of_scope`      — explicitly not pursued (HIPAA in v1, FedRAMP)
 *
 * **Type-only schema (Lead Dev hat, locked)**: types declared
 * statically; runtime validation lives in
 * `compliance-status.test.ts` so we don't ship a zod runtime to
 * the marketing bundle. Same pattern as iter-243's
 * `subprocessors.ts` loader.
 *
 * **Three-layer contract preserved**: pure presentational data —
 * no runtime fetch. Compile-time JSON import is fine because the
 * file is small + changes infrequently. Admin edits ship via a PR
 * that triggers an Amplify rebuild — same flow as a code change.
 */

export type ComplianceStatus =
  | 'planned'
  | 'in_progress'
  | 'available'
  | 'compliance_verified'
  | 'certified'
  | 'out_of_scope';

export interface ComplianceFramework {
  /** i18n key for the framework display name (acronyms/dates kept verbatim inside). */
  readonly labelKey: string;
  readonly status: ComplianceStatus;
  /** i18n key for the target, or null when there's no target. */
  readonly targetKey: string | null;
  readonly evidence_provider: string | null;
  readonly auditor: string | null;
  readonly notesKey: string;
}

export interface ComplianceStatusDocument {
  readonly lastUpdated: string;
  readonly frameworks: Readonly<Record<string, ComplianceFramework>>;
}

export const complianceStatusDocument: ComplianceStatusDocument = data as ComplianceStatusDocument;

export const complianceFrameworks: readonly (readonly [string, ComplianceFramework])[] =
  Object.entries(complianceStatusDocument.frameworks);
